import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@material-ui/core';

import { useBackendContext } from '@omq/shared';

import { EMAIL_VALIDATION_PATTERN } from '@admin/conf';

import { Link } from '@admin/shared/components/router/link';
import { Form, FormRow } from '@admin/shared/components/forms/form';
import { Headline } from '@admin/shared/components/typography/headline';
import { TextField } from '@admin/shared/components/inputs/text-field/text-field';
import { useDialogContext } from '@admin/shared/context/dialog';

import { LoginAPI } from '@admin/shared/api/login';
import { LockIcon } from '@admin/shared/components/icons/custom/lock';
import { DialogSizes, useDialogStyles } from '@admin/shared/components/dialogs/dialog';
import { LinkButton } from '@admin/shared/components/buttons/link-button/link-button';
import type { ValidationState } from '@admin/shared/util/validation';
import { Validation } from '@admin/shared/util/validation';

import { LoadingIndicator } from '@admin/shared/components/progress/loading-indicator';
import { LOGIN_ROUTES } from '@admin/login/login';

// form validation config
const validationConfig = {
  email: {
    isRequired: true,
    minLength: 3,
    pattern: EMAIL_VALIDATION_PATTERN,
  },
};

/**
 * Renders form to retrieve password.
 *
 * @author Florian Walch
 * @since 9.7
 *
 * @returns {JSX.Element}
 */
export const ForgotPasswordForm = (): JSX.Element => {
  const dialogStyles = useDialogStyles();
  const { t, i18n } = useTranslation(['login']);
  const backend = useBackendContext();
  const { showDialog } = useDialogContext();

  // set up states
  const [username, setUsername] = useState<string>('');
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // validate form input
  const handleFormValidation = () => {
    const error = Validation.validateValueWithConfig(username, validationConfig.email);

    if (error != null) {
      setValidationState({
        email: error,
      });
      return false;
    } else {
      setValidationState({});
    }

    return true;
  };

  // handle for submit
  const handleFormSubmit = async (evt) => {
    evt.preventDefault();

    const language = i18n.language.substr(0, 2).toUpperCase();

    // send username & language to server
    try {
      setIsLoading(true);

      await LoginAPI.resetPassword(backend, username, language).finally(() => {
        setIsLoading(false);
      });

      // show success text
      setFormSubmitted(true);
    } catch (ex) {
      // show error
      showDialog({
        size: DialogSizes.SMALL,
        children: (
          <Box display="flex" flexDirection="column" alignItems="center">
            <LockIcon className={dialogStyles.icon} />
            <p className={dialogStyles.headline}>{t('dialogs.passwordResetFailed.title')}</p>
            <p className={dialogStyles.text}>{t('dialogs.passwordResetFailed.text')}</p>
          </Box>
        ),
        actions: [
          {
            label: t('login:buttons.ok'),
            color: 'primary',
            autoFocus: true,
          },
        ],
      });
    }
  };

  let formContent = null;

  // show result if form has been submitted
  if (formSubmitted) {
    formContent = (
      <Form>
        <FormRow margin="large">
          <Headline size="large">{t('login:headlines.forgotPasswordResult')}</Headline>
          <Box mt={3}>
            <Typography variant="body1">{t('login:texts.forgotPasswordResult')}</Typography>
          </Box>
        </FormRow>

        <FormRow>
          <Link to={LOGIN_ROUTES.LOGIN}>
            <Button variant="contained" color="primary">
              {t('login:links.login')}
            </Button>
          </Link>
        </FormRow>
      </Form>
    );
  } else {
    formContent = (
      <Form onSubmit={handleFormSubmit} customValidation={handleFormValidation}>
        <FormRow margin="large">
          <Headline size="large">{t('login:headlines.forgotPassword')}</Headline>
          <Box mt={3}>
            <Typography variant="body1">{t('login:texts.forgotPassword')}</Typography>
          </Box>
        </FormRow>

        <FormRow margin="large">
          <TextField
            placeholder={t('login:inputs.mail')}
            validationConfig={validationConfig.email}
            errorMessage={validationState.email}
            autoComplete="username"
            type="email"
            value={username}
            onChange={(evt) => setUsername(evt.target.value)}
          />
        </FormRow>

        <FormRow>
          <Button variant="contained" color="primary" type="submit">
            {t('login:buttons.continue')}
          </Button>

          <Box display="inline-block" ml={11}>
            <Link to={LOGIN_ROUTES.LOGIN}>
              <LinkButton>{t('login:links.login')}</LinkButton>
            </Link>
          </Box>
        </FormRow>
      </Form>
    );
  }

  // show form
  return (
    <Box>
      {formContent}
      {isLoading && <LoadingIndicator />}
    </Box>
  );
};
